/* eslint-disable no-console */
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { ConnectedQueryParamUpdateProvider,QueryParamUpdateProviderOwnProps } from '~src/bootstrap/routerHelpers/QueryParamUpdateProvider';
import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { Messages } from '~src/codegen/Localize';
import { MapDispatchToProps } from '~src/commonInfra/utils/ConnectUtils';
import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { enterSpace, setSpaceInfoItem } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { updateBreadcrumbs } from '~src/spacesPlatform/actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { GroupSetSubspaceName, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { IGroupSetBasicInfo } from '../../models/IGroupSetInfo';
import { getGroupSetById } from '../../serverApi/requests/GetGroupSetRequest';
import { ConnectedGroupSetSpace } from './ConnectedGroupSetSpace';
import { GroupSetSpaceOwnProps } from './GroupSetSpace';


export interface GroupSetSpaceProviderOwnProps {
    readonly siteName: string;
    readonly subspaceName: GroupSetSubspaceName;
    readonly groupSetId: string;
    readonly routeLocationSearch: string;
    readonly urls: SpacesSiteUrls;
    readonly renderSpace: (props: GroupSetSpaceOwnProps) => JSX.Element;
}

export interface LocationProps {
    readonly routeLocationPath: string;
    readonly routeLocationSearch: string;
}

export type GroupSetSpaceProviderProps = LocationProps & Omit<GroupSetSpaceProviderOwnProps, 'renderSpace'>;

export function GroupSetSpaceProvider(props: GroupSetSpaceProviderProps) {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath: props.routeLocationPath,
      routeLocationSearch: props.routeLocationSearch,
      shouldUpdateFilters: true,
      shouldUpdateContentTypes: false,
      shouldUpdateSort: true,
      shouldUpdateBreadcrumbs: false
    };

    const spaceInfo: SpaceInfo = {
      item: null,
      spaceType: 'groupset',
      subspaceName: props.subspaceName
    };

    const spaceProps = {
      siteName: props.siteName,
      groupSetId: props.groupSetId,
      subspaceName: props.subspaceName,
      routeLocationSearch: props.routeLocationSearch,
      urls: props.urls,
      renderSpace
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedGroupSetSpaceProviderContainer {...spaceProps} />
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
}

const renderSpace = (props: GroupSetSpaceOwnProps): JSX.Element => <ConnectedGroupSetSpace {...props} />;

export interface GroupSetSpaceProviderDispatchProps {
    readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
    readonly enterSpace: (spaceInfo: SpaceInfo) => void;
    readonly requestSpaceContents: () => void;
    readonly contentCountsUpToDate: () => void;
    readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
    readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

const mapDispatchToProps: MapDispatchToProps<GroupSetSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
    setSpaceInfoItem,
    requestSpaceContents,
    enterSpace,
    contentCountsUpToDate,
    setOrderAction,
    updateBreadcrumbs,
  }, dispatch);

export interface GroupSetSpaceProviderStateProps {}
export interface GroupSetSpaceProviderState {
    readonly initialRender: boolean;
    readonly renderChildren: boolean;
    readonly hasError: boolean;
    readonly groupSetInfo: IGroupSetBasicInfo;
}
const InitialState: GroupSetSpaceProviderState = {
    initialRender: true,
    renderChildren: false,
    hasError: false,
    groupSetInfo: undefined,
};

export type GroupSetSpaceProviderContainerProps = GroupSetSpaceProviderOwnProps & GroupSetSpaceProviderStateProps & GroupSetSpaceProviderDispatchProps;

export class GroupSetSpaceProviderContainer extends React.PureComponent<GroupSetSpaceProviderContainerProps, GroupSetSpaceProviderState> {

    private unmounted: boolean = false;

    public constructor(props: GroupSetSpaceProviderContainerProps) {
      super(props);
      this.state = InitialState;
    }

    public render() {
      if (this.state.renderChildren && this.haveAllDataNeededToRender()) {
        const props: GroupSetSpaceOwnProps = {
          urls: this.props.urls,
          reloadSpace: this.reloadSpace,
        };
        return this.props.renderSpace(props);
      } else if (this.state.hasError) {
        return <RouterError errorType={'groupset'} urls={this.props.urls} />;
      } else {
        return null;
      }
    }

    public componentDidMount(){
      this.getData();
    }

    public componentDidUpdate(prevProps: GroupSetSpaceProviderContainerProps) {
      if (this.changedGroup(prevProps)) {
        this.updateState(InitialState /* initial state for new group */);
        this.getData();
        return;
      }

      // if we dont have all we need - do nothing
      if (!this.haveAllDataNeededToRender()) {
        return;
      }

      if (this.state.initialRender) {
        this.changeSubspace();
        this.setState({initialRender: false, renderChildren: true});
      } else if (this.props.subspaceName !== prevProps.subspaceName) {
        this.changeSubspace();
      }
    }

    public componentWillUnmount() {
      this.unmounted = true;
    }

    private changeSubspace() {
      this.setSpaceInfoAndEnterSpace();
      this.props.contentCountsUpToDate();

      this.props.requestSpaceContents();

    }

    private setSpaceInfoAndEnterSpace() {
      this.props.enterSpace(
        {
          item: this.state.groupSetInfo,
          spaceType: 'groupset',
          subspaceName: this.props.subspaceName
        }
      );
    }

    private changedGroup(prevProps: GroupSetSpaceProviderContainerProps) {
      return !_.isUndefined(this.props.siteName) &&
        !_.isUndefined(this.props.groupSetId) &&
        (prevProps.siteName !== this.props.siteName || prevProps.groupSetId !== this.props.groupSetId);
    }

    private haveAllDataNeededToRender() {
      return !_.isUndefined(this.state.groupSetInfo);
    }

    private getData = async () => {
      try {
        let groupSet = await getGroupSetById(this.props.groupSetId);
        this.updateState({
            groupSetInfo: groupSet,
        });
        this.props.updateBreadcrumbs([getGroupSetsPlaceBreadcrumb(this.props.urls), {text: groupSet.name}]);
      } catch (e) {
        this.updateState({hasError: true});
      }
    };

    private updateState<K extends keyof GroupSetSpaceProviderState>(newState: Pick<GroupSetSpaceProviderState, K>, callback?: () => void): void {
      if (!this.unmounted) {
        this.setState(newState, callback);
      }
    }

    private reloadSpace = async () => {
      await this.getData();
      if (this.props.subspaceName === 'groups') {
        this.props.setSpaceInfoItem(
          {
            item: this.state.groupSetInfo,
            spaceType: 'groupset',
            subspaceName: this.props.subspaceName
          }
        );
      }
    };
}

export const ConnectedGroupSetSpaceProviderContainer: React.ComponentClass<GroupSetSpaceProviderOwnProps>
    = connect(undefined, mapDispatchToProps)(GroupSetSpaceProviderContainer);

export function getGroupSetsPlaceBreadcrumb(urls: SpacesSiteUrls): BreadcrumbStateInfo {
    const href = urls.groupSetsUrl();
    return {
        id: href,
        text: Messages.allGroupSets(),
        href: href,
        testId: 'group-sets-link'
    };
}